import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import DeleteIcon from '@mui/icons-material/Delete';
import JsonView from '@uiw/react-json-view';
import Tooltip from '@mui/material/Tooltip';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  deleteItem,
  fetchItems,
  patchItemRaw,
  postItemRaw,
  putItemRaw,
} from '../../../support/queries';
import { PathAndParamsEditor } from './PathAndParamsEditor';
import { substituteVariables } from '../../../support/customTileTransformUtils';
import { shouldIncludePageSize } from '../../../support/customTileHelpers';
import { getLocalJsonFiles, getLocalJsonFileByPath } from '../../../support/localFileRegistry';
import { getApiHeaders } from '../../../support/queriesHelpers';
import { useTileSourceCatalog } from '../../../hooks/toolbox-hooks';
import { useFetchCampaigns, useFetchDeliveries } from '../../../hooks/campaign-hooks';
import {
  CAMPAIGN_DELIVERY_SOURCE,
  resolveCampaignDeliveryData,
  CampaignDeliveryDataset,
} from '../../../support/dataImport/campaignChartSource';
import { Query } from '../../../types/tileBuilderTypes';
import { RootState } from '../../../support/store';

const FALLBACK_HTTP_METHODS = ['GET', 'POST'];
const NONE_ENTITY = '';
const LOCAL_FILE_LABEL = 'Local File';
const TOOLBOX_DB_LABEL = 'Toolbox DB';

function normalizeQuery(q: Query) {
  return {
    dataSource: q.dataSource ?? '',
    model: q.model ?? '',
    entity: q.entity ?? '',
    function: q.function ?? '',
    method: q.method ?? 'GET',
    path: q.path ?? '',
    params: q.params ?? {},
    payload: q.payload ?? '',
    queryResult: q.queryResult,
    functionResult: q.functionResult,
    runtimeParams: q.runtimeParams ?? {},
    conditionalExecution: q.conditionalExecution,
    dependsOn: q.dependsOn ?? [],
    transformFunction: q.transformFunction ?? '',
    useResultFrom: q.useResultFrom,
    resultMapping: q.resultMapping ?? {},
    localFilePath: q.localFilePath ?? '',
    localFileName: q.localFileName ?? '',
    ...q,
  };
}

function getEmailPrefix(value: string | undefined | null) {
  if (!value) return '';
  return String(value).split('@')[0].trim().toLowerCase();
}

function resolveLocalFileData(
  fileData: any,
  rawPath: string,
  availableVariables: Record<string, string>
) {
  if (fileData == null) return undefined;

  if (!rawPath || !rawPath.trim()) {
    return fileData;
  }

  const substitutedPath = substituteVariables(rawPath, availableVariables).trim();

  if (!substitutedPath) {
    return fileData;
  }

  const arrayData = Array.isArray(fileData) ? fileData : [fileData];

  const lastSegment = substitutedPath.split('/').filter(Boolean).pop() || '';
  const emailPrefix = getEmailPrefix(lastSegment);

  if (!emailPrefix) {
    return fileData;
  }

  const matchedUser = arrayData.find((item: any) => {
    const empCecid = String(item?.EMP_CECID ?? '').toLowerCase();
    const empCecidDisp = String(item?.EMP_CECID_DISP ?? '').toLowerCase();
    const leaderCecid = String(item?.LDR_CECID ?? '').toLowerCase();

    return (
      empCecid === emailPrefix ||
      empCecidDisp === emailPrefix ||
      leaderCecid === emailPrefix
    );
  });

  if (!matchedUser) {
    return fileData;
  }

  if (Array.isArray(matchedUser?.reports)) {
    return matchedUser.reports;
  }

  return matchedUser;
}

const QueryRow = ({ chartId, query, onUpdate, onDelete, allQueries }) => {
  const userData: any = useSelector((state: RootState) => state.user.userData);
  const previewRuntimeContext: any = useSelector(
    (state: RootState) => state.tileBuilder[chartId]?.preview?.runtimeContext || {}
  );

  const availableVariables: Record<string, string> = useMemo(() => {
    return {
      name: userData?.name ?? '',
      email:
        previewRuntimeContext?.email ??
        previewRuntimeContext?.userEmail ??
        previewRuntimeContext?.ownerEmail ??
        userData?.email ??
        '',
      sfId: previewRuntimeContext?.sfId ?? userData?.sfId ?? '',
      managerEmail: previewRuntimeContext?.managerEmail ?? '',
      current_time_filter: previewRuntimeContext?.current_time_filter ?? 'all',
    };
  }, [userData, previewRuntimeContext]);

  const { data: apiData } = useQuery({
    queryKey: ['models'],
    queryFn: () => fetchItems('db/tables'),
  });
  const models = useMemo(() => apiData?.models ?? [], [apiData]);

  const {
    dataSources,
    sourcesForSelect,
    sourceMapByLabel,
    isLoading: sourceCatalogLoading,
  } = useTileSourceCatalog();

  const toolboxEntityOptions = useMemo(
    () =>
      models.map((m: any) => ({
        value: m.db_table,
        label: m.verbose_name || m.db_table,
        path: `db/${m.db_table}`,
      })),
    [models]
  );

  const entityLists = useMemo(() => {
    const lists: Record<string, { value: string; label: string; path?: string }[]> = {
      [TOOLBOX_DB_LABEL]: toolboxEntityOptions,
    };

    (dataSources || []).forEach((src: any) => {
      if (src.label === TOOLBOX_DB_LABEL) return;
      lists[src.label] = (src.entities || []).map((e: any) => ({
        value: e.value,
        label: e.label || e.value,
        path: e.path,
      }));
    });

    if (!lists[LOCAL_FILE_LABEL]) {
      lists[LOCAL_FILE_LABEL] = [];
    }

    return lists;
  }, [dataSources, toolboxEntityOptions]);

  const initialSource =
    query.dataSource || sourcesForSelect?.[0]?.value || TOOLBOX_DB_LABEL;

  const [source, setSource] = useState(initialSource);
  const [entity, setEntity] = useState(query.model || query.entity || '');
  const [model, setModel] = useState(query.model || query.entity || '');
  const [path, setPath] = useState(query.path || '');
  const [params, setParams] = useState(query.params || {});
  const [method, setMethod] = useState(query.method || 'GET');
  const [payload, setPayload] = useState(query.payload ?? '');
  const [localFilePath, setLocalFilePath] = useState(query.localFilePath || '');
  const [runtimeParams, setRuntimeParams] = useState(query.runtimeParams || {});
  const [conditionalExecution, setConditionalExecution] = useState(query.conditionalExecution);
  const [showRuntimeParams, setShowRuntimeParams] = useState(false);
  const [enableDateFilter, setEnableDateFilter] = useState(query.enableDateFilter);

  // Campaign Delivery source (synthetic, resolved at render time by campaignChartSource).
  const [cdCampaignId, setCdCampaignId] = useState<number | ''>(
    query.campaignDeliveryCampaignId ?? ''
  );
  const [cdDeliveryId, setCdDeliveryId] = useState<number | ''>(
    query.campaignDeliveryId ?? ''
  );
  const [cdDataset, setCdDataset] = useState<CampaignDeliveryDataset>(
    query.campaignDeliveryDataset ?? 'step_status'
  );

  const isCampaignDelivery = source === CAMPAIGN_DELIVERY_SOURCE;
  const { data: cdCampaigns = [] } = useFetchCampaigns(undefined, {
    enabled: isCampaignDelivery,
  });
  const { data: cdDeliveries = [] } = useFetchDeliveries(cdCampaignId || undefined, {
    enabled: isCampaignDelivery && !!cdCampaignId,
  });

  const defaultedRef = useRef(false);

  const selectedSourceConfig = sourceMapByLabel?.[source];
  const selectedEntityConfig = (selectedSourceConfig?.entities || []).find(
    (e: any) => e.value === entity
  );

  const allowedMethods =
    selectedEntityConfig?.allowed_methods?.length
      ? selectedEntityConfig.allowed_methods
      : selectedSourceConfig?.allowed_methods?.length
        ? selectedSourceConfig.allowed_methods
        : FALLBACK_HTTP_METHODS;

  const localFiles = useMemo(() => {
    return source === LOCAL_FILE_LABEL ? getLocalJsonFiles() : [];
  }, [source]);

  const entityValue = useMemo(() => {
    return query.model ?? query.entity ?? '';
  }, [query.model, query.entity]);

  const modelValue = useMemo(() => {
    return query.model ?? query.entity ?? '';
  }, [query.model, query.entity]);

  // Stable keys extracted from inline JSON.stringify calls so dependency arrays can be
  // statically checked (values are unchanged).
  const queryParamsKey = JSON.stringify(query.params);
  const queryRuntimeParamsKey = JSON.stringify(query.runtimeParams);
  const queryConditionalExecutionKey = JSON.stringify(query.conditionalExecution);
  const sourcesForSelectKey = JSON.stringify(sourcesForSelect);
  const entityListsKey = JSON.stringify(entityLists);
  const paramsKey = JSON.stringify(params);
  const runtimeParamsKey = JSON.stringify(runtimeParams);
  const conditionalExecutionKey = JSON.stringify(conditionalExecution);

  useEffect(() => {
    if (!query.dataSource && !source && sourcesForSelect?.length > 0) {
      setSource(sourcesForSelect[0].value);
    }
  }, [query.dataSource, source, sourcesForSelect]);

  useEffect(() => {
    setSource(query.dataSource || sourcesForSelect?.[0]?.value || TOOLBOX_DB_LABEL);

    const isNewQuery =
      !query.path &&
      !query.entity &&
      !query.model &&
      !query.method &&
      !Object.keys(query.params || {}).length &&
      !query.payload &&
      !query.localFilePath;

    if (isNewQuery && !defaultedRef.current) {
      let foundEntity = '';
      if (source === TOOLBOX_DB_LABEL && models.length > 0) {
        foundEntity = models[0].db_table;
      } else if (entityLists[source]?.length > 0) {
        foundEntity = entityLists[source][0].value;
      }

      if (foundEntity) {
        setEntity(foundEntity);
        setModel(foundEntity);
        defaultedRef.current = true;
      }
    } else {
      setEntity(entityValue);
      setModel(modelValue);
    }

    setPath(query.path || '');
    setParams(query.params || {});
    setMethod(query.method || 'GET');
    setPayload(query.payload ?? '');
    setLocalFilePath(query.localFilePath || '');
    setRuntimeParams(query.runtimeParams || {});
    setConditionalExecution(query.conditionalExecution);
    setEnableDateFilter(query.enableDateFilter ?? false);

    // eslint-disable-next-line
  }, [
    query.dataSource,
    query.path,
    queryParamsKey,
    query.method,
    query.payload,
    query.localFilePath,
    queryRuntimeParamsKey,
    queryConditionalExecutionKey,
    query.enableDateFilter,
    source,
    models,
    sourcesForSelectKey,
    entityListsKey,
  ]);

  useEffect(() => {
    if (entity !== model) setModel(entity);
    // eslint-disable-next-line
  }, [entity]);

  useEffect(() => {
    if (!entity) return;
    if (path && path.trim()) return;
    if (source === LOCAL_FILE_LABEL) return;

    if (source === TOOLBOX_DB_LABEL) {
      setPath(`db/${entity}`);
      return;
    }

    const found = (entityLists[source] || []).find((ent) => ent.value === entity);
    if (found?.path) {
      setPath(found.path);
    }
    // eslint-disable-next-line
  }, [entity, source, entityLists]);

  useEffect(() => {
    if (source === LOCAL_FILE_LABEL && !localFilePath && localFiles.length > 0) {
      setLocalFilePath(localFiles[0].path);
    }
  }, [source, localFilePath, localFiles]);

  useEffect(() => {
    if (source !== LOCAL_FILE_LABEL) return;

    const file = getLocalJsonFileByPath(localFilePath);
    const resolvedData = resolveLocalFileData(file?.data, path, availableVariables);

    onUpdate(
      normalizeQuery({
        ...query,
        dataSource: source,
        entity: '',
        model: localFilePath.split('/').pop()?.replace(/\.json$/i, '') || LOCAL_FILE_LABEL,
        path,
        params: {},
        method: 'GET',
        payload: '',
        runtimeParams,
        conditionalExecution,
        localFilePath,
        localFileName: localFilePath.split('/').pop() || '',
        queryResult: resolvedData,
        enableDateFilter,
      }),
    );
    // eslint-disable-next-line
  }, [source, localFilePath, path, JSON.stringify(availableVariables), runtimeParams, conditionalExecution, enableDateFilter]);

  const entityOptions = useMemo(() => {
    let opts: { value: string; label: string; path?: string }[] = entityLists[source] || [];
    if (entity && !opts.some((opt) => opt.value === entity)) {
      opts = [{ value: entity, label: entity }, ...opts];
    }
    return opts;
  }, [source, entityLists, entity]);

  const handleEntityChange = (e: SelectChangeEvent<any>) => {
    const next = e.target.value as string;

    setEntity(next);
    setModel(next);

    if (!next) return;

    if (source === TOOLBOX_DB_LABEL) {
      setPath(`db/${next}`);
      return;
    }

    const found = (entityLists[source] || []).find((ent) => ent.value === next);
    if (found?.path) {
      setPath(found.path);
    }
  };

  const handleSourceChange = (e: SelectChangeEvent<any>) => {
    const newSource = e.target.value as string;
    setSource(newSource);
    setEntity('');
    setModel('');
    setPath(newSource === LOCAL_FILE_LABEL ? '{email}' : '');
    setParams({});
    setPayload('');
    setLocalFilePath('');
    setMethod('GET');
    setCdCampaignId('');
    setCdDeliveryId('');
    setCdDataset('step_status');
    defaultedRef.current = false;
  };

  useEffect(() => {
    setMethod(query.method || 'GET');
    setPayload(query.payload ?? '');
    // eslint-disable-next-line
  }, [query.method, query.payload]);

  useEffect(() => {
    // The Campaign Delivery source has its own onUpdate effect below (async row resolution).
    if (source === CAMPAIGN_DELIVERY_SOURCE) return;
    onUpdate(
      normalizeQuery({
        ...query,
        dataSource: source,
        entity: source === LOCAL_FILE_LABEL ? '' : entity,
        model:
          source === LOCAL_FILE_LABEL
            ? localFilePath.split('/').pop()?.replace(/\.json$/i, '') || LOCAL_FILE_LABEL
            : model,
        path,
        params: source === LOCAL_FILE_LABEL ? {} : params,
        method: source === LOCAL_FILE_LABEL ? 'GET' : method,
        payload: source === LOCAL_FILE_LABEL ? '' : payload,
        runtimeParams,
        conditionalExecution,
        localFilePath: source === LOCAL_FILE_LABEL ? localFilePath : '',
        localFileName:
          source === LOCAL_FILE_LABEL ? localFilePath.split('/').pop() || '' : '',
        enableDateFilter,
        // Clear Campaign Delivery carriers when this row is any other source.
        campaignDeliveryId: undefined,
        campaignDeliveryDataset: undefined,
        campaignDeliveryCampaignId: undefined,
      }),
    );
    // eslint-disable-next-line
  }, [
    source,
    entity,
    model,
    path,
    paramsKey,
    method,
    payload,
    localFilePath,
    runtimeParamsKey,
    conditionalExecutionKey,
    enableDateFilter,
  ]);

  // Campaign Delivery source: resolve the selected delivery's rows and write the carrier fields.
  // Mirrors the Local File effect but resolves asynchronously (live campaign tables, not a
  // persisted dataset). queryResult is populated inline so the builder preview renders immediately;
  // persisted tiles re-resolve at render time via customTileDataFetcher.
  useEffect(() => {
    if (!isCampaignDelivery) return;
    let cancelled = false;

    const base = {
      ...query,
      dataSource: CAMPAIGN_DELIVERY_SOURCE,
      entity: '',
      model: 'Campaign Delivery',
      path: '',
      params: {},
      method: 'GET' as const,
      payload: '',
      localFilePath: '',
      localFileName: '',
      runtimeParams,
      conditionalExecution,
      enableDateFilter,
      campaignDeliveryCampaignId: cdCampaignId || undefined,
      campaignDeliveryId: cdDeliveryId || undefined,
      campaignDeliveryDataset: cdDataset,
    };

    if (!cdDeliveryId) {
      onUpdate(normalizeQuery({ ...base, queryResult: undefined }));
      return;
    }

    resolveCampaignDeliveryData({
      dataSource: CAMPAIGN_DELIVERY_SOURCE,
      campaignDeliveryId: Number(cdDeliveryId),
      campaignDeliveryDataset: cdDataset,
    })
      .then((rows) => {
        if (cancelled) return;
        onUpdate(normalizeQuery({ ...base, queryResult: rows }));
      })
      .catch(() => {
        if (cancelled) return;
        onUpdate(normalizeQuery({ ...base, queryResult: [] }));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [
    isCampaignDelivery,
    cdCampaignId,
    cdDeliveryId,
    cdDataset,
    runtimeParamsKey,
    conditionalExecutionKey,
    enableDateFilter,
  ]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewReq, setPreviewReq] = useState<any | null>(null);
  const [previewResp, setPreviewResp] = useState<any | null>(null);
  const [tab, setTab] = useState(0);

  async function handlePreview() {
    setTab(0);
    setPreviewOpen(true);
    setPreviewResp(null);
    setPreviewLoading(true);

    if (isCampaignDelivery) {
      const req = {
        url: cdDataset === 'target' ? 'db/campaign-delivery-target' : 'db/campaign-delivery-step-status',
        method: 'GET',
        headers: {},
        params: { delivery: cdDeliveryId || '' },
        body: undefined,
      };
      setPreviewReq(req);
      try {
        if (!cdDeliveryId) {
          setPreviewResp({ body: [], headers: {}, status: 200, statusText: 'OK' });
          return;
        }
        const rows = await resolveCampaignDeliveryData({
          dataSource: CAMPAIGN_DELIVERY_SOURCE,
          campaignDeliveryId: Number(cdDeliveryId),
          campaignDeliveryDataset: cdDataset,
        });
        setPreviewResp({ body: rows, headers: {}, status: 200, statusText: 'OK' });
      } catch (err: any) {
        setPreviewResp({ error: err?.message || String(err) });
      } finally {
        setPreviewLoading(false);
      }
      return;
    }

    if (source === LOCAL_FILE_LABEL) {
      try {
        const file = getLocalJsonFileByPath(localFilePath);
        const resolvedData = resolveLocalFileData(file?.data, path, availableVariables);

        setPreviewReq({
          url: localFilePath,
          method: 'GET',
          headers: {},
          params: {},
          body: undefined,
        });

        setPreviewResp({
          body: resolvedData ?? {},
          headers: {},
          status: 200,
          statusText: 'OK',
        });
      } catch (err: any) {
        setPreviewReq({
          url: localFilePath,
          method: 'GET',
          headers: {},
          params: {},
          body: undefined,
        });

        setPreviewResp({
          error: err.message || String(err),
        });
      } finally {
        setPreviewLoading(false);
      }

      return;
    }

    const effectiveParams = {
      ...(params || {}),
      ...(runtimeParams || {}),
    };

    const endpoint = substituteVariables(path?.replace(/^\//, '') || '', availableVariables);
    const previewParams: Record<string, string> = {};

    for (const [k, v] of Object.entries(effectiveParams || {})) {
      const substitutedValue = substituteVariables(String(v), availableVariables).trim();
      if (!substitutedValue || /^\{.+\}$/.test(substitutedValue)) continue;
      previewParams[k] = substitutedValue;
    }

    const reqMethod = (method || 'GET').toUpperCase();
    const token = Cookies.get('sf_token') || null;

    const previewRequest = {
      url: endpoint,
      method: reqMethod,
      headers: getApiHeaders(token, reqMethod, endpoint),
      params: previewParams,
      body: undefined as any,
    };

    try {
      let resp: any = null;
      let urlUsed = '';
      const responseHeaders = {};
      const responseStatus = undefined;
      const responseStatusText = undefined;

      if (['GET'].includes(reqMethod)) {
        resp = await fetchItems(endpoint, previewParams, {
          token,
          includePageSize: shouldIncludePageSize(endpoint),
        });
        urlUsed = `${process.env.REACT_APP_API}/${endpoint}${
          Object.keys(previewParams).length
            ? `?${new URLSearchParams(previewParams).toString()}`
            : ''
        }`;
      } else if (['POST'].includes(reqMethod)) {
        let body: any = payload;
        try {
          body = body && typeof body === 'string' ? JSON.parse(body) : body;
        } catch {
          console.log('body', body);
        }
        previewRequest.body = body;
        const substitutedBody =
          typeof body === 'string' ? substituteVariables(body, availableVariables) : body;
        resp = await postItemRaw(endpoint, substitutedBody);
        urlUsed = `${process.env.REACT_APP_API}/${endpoint}`;
      } else if (['PUT'].includes(reqMethod)) {
        let body: any = payload;
        try {
          body = body && typeof body === 'string' ? JSON.parse(body) : body;
        } catch {
          console.log('body', body);
        }
        previewRequest.body = body;
        resp = await putItemRaw(endpoint, body);
        urlUsed = `${process.env.REACT_APP_API}/${endpoint}`;
      } else if (['PATCH'].includes(reqMethod)) {
        let body: any = payload;
        try {
          body = body && typeof body === 'string' ? JSON.parse(body) : body;
        } catch {
          console.log('body', body);
        }
        previewRequest.body = body;
        resp = await patchItemRaw(endpoint, body);
        urlUsed = `${process.env.REACT_APP_API}/${endpoint}`;
      } else if (['DELETE'].includes(reqMethod)) {
        resp = await deleteItem(endpoint, token);
        urlUsed = `${process.env.REACT_APP_API}/${endpoint}`;
      } else {
        throw new Error(`Unsupported method: ${reqMethod}`);
      }

      setPreviewReq({
        ...previewRequest,
        url: urlUsed,
      });
      setPreviewResp({
        body: resp,
        headers: responseHeaders,
        status: responseStatus,
        statusText: responseStatusText,
      });
    } catch (err: any) {
      setPreviewReq(previewRequest);
      setPreviewResp({
        error: err.message || String(err),
      });
    } finally {
      setPreviewLoading(false);
    }
  }

  const isNotGet = source !== LOCAL_FILE_LABEL && method && method.toUpperCase() !== 'GET';
  const tabLabels = isNotGet
    ? ['Request Headers', 'Request Body', 'Response Headers', 'Response Body']
    : ['Request Headers', 'Response Headers', 'Response Body'];

  const entityLabel = source === TOOLBOX_DB_LABEL ? 'Table/Model' : 'Entity';
  const runtimeParamEntries = Object.entries(runtimeParams || {});

  const handleRuntimeParamKeyChange = (idx: number, newKey: string) => {
    const entries = [...runtimeParamEntries];
    entries[idx] = [newKey, entries[idx][1]];
    setRuntimeParams(Object.fromEntries(entries));
  };

  const handleRuntimeParamValueChange = (idx: number, newValue: string) => {
    const entries = [...runtimeParamEntries];
    entries[idx] = [entries[idx][0], newValue];
    setRuntimeParams(Object.fromEntries(entries));
  };

  const handleAddRuntimeParam = () => {
    setRuntimeParams({ ...runtimeParams, '': '{managerEmail}' });
  };

  const handleRemoveRuntimeParam = (idx: number) => {
    const entries = [...runtimeParamEntries];
    entries.splice(idx, 1);
    setRuntimeParams(Object.fromEntries(entries));
  };

  const toggleDateFilter = (enabled: boolean): void => {
    setEnableDateFilter(enabled);
    if (enabled) {
      setRuntimeParams({
        ...runtimeParams,
        current_time_filter: '{current_time_filter}',
      });
    } else {
      const newRuntimeParams = { ...runtimeParams };
      delete newRuntimeParams.current_time_filter;
      setRuntimeParams(newRuntimeParams);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} mb={2}>
      <Box display="flex" gap={2}>
        <FormControl fullWidth disabled={sourceCatalogLoading}>
          <InputLabel>Data Source</InputLabel>
          <Select value={source} label="Data Source" onChange={handleSourceChange}>
            {(sourcesForSelect || []).map((src: any) => (
              <MenuItem key={src.value} value={src.value}>
                {src.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isCampaignDelivery ? (
          <>
            <FormControl fullWidth>
              <InputLabel id="cd-campaign-label">Campaign</InputLabel>
              <Select
                labelId="cd-campaign-label"
                value={cdCampaignId === '' ? '' : cdCampaignId}
                label="Campaign"
                onChange={(e) => {
                  setCdCampaignId(e.target.value === '' ? '' : Number(e.target.value));
                  setCdDeliveryId('');
                }}
              >
                {(cdCampaigns || []).map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name || `Campaign ${c.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!cdCampaignId}>
              <InputLabel id="cd-delivery-label">Delivery</InputLabel>
              <Select
                labelId="cd-delivery-label"
                value={cdDeliveryId === '' ? '' : cdDeliveryId}
                label="Delivery"
                onChange={(e) =>
                  setCdDeliveryId(e.target.value === '' ? '' : Number(e.target.value))
                }
              >
                {(cdDeliveries || []).map((d: any) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name || d.customer_name || `Delivery ${d.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="cd-dataset-label">Dataset</InputLabel>
              <Select
                labelId="cd-dataset-label"
                value={cdDataset}
                label="Dataset"
                onChange={(e) => setCdDataset(e.target.value as CampaignDeliveryDataset)}
              >
                <MenuItem value="step_status">Step Completion</MenuItem>
                <MenuItem value="target">Targets (per customer)</MenuItem>
              </Select>
            </FormControl>
          </>
        ) : source === LOCAL_FILE_LABEL ? (
          <FormControl fullWidth>
            <InputLabel id="local-file-label">Local File</InputLabel>
            <Select
              labelId="local-file-label"
              value={localFilePath}
              label="Local File"
              onChange={(e) => setLocalFilePath(e.target.value)}
            >
              {localFiles.map((file: any) => (
                <MenuItem key={file.path} value={file.path}>
                  {file.name || file.path}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="entity-label" shrink>
              {entityLabel}
            </InputLabel>
            <Select
              labelId="entity-label"
              value={entity ?? ''}
              label={entityLabel}
              onChange={handleEntityChange}
              displayEmpty
              renderValue={(selected) => (selected ? selected : 'None (Custom Path)')}
            >
              <MenuItem value={NONE_ENTITY}>
                <em>None (Custom Path)</em>
              </MenuItem>

              {entityOptions.map((e) => (
                <MenuItem key={e.value} value={e.value}>
                  {e.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!isCampaignDelivery && Object.keys(availableVariables).length > 0 && (
          <Box>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Available Variables:</div>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {Object.entries(availableVariables).map(([k, v]) => (
                <Tooltip key={k} title={v ? v : '(empty)'} arrow>
                  <Chip label={`{${k}}`} size="small" sx={{ cursor: 'pointer' }} />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {!isCampaignDelivery && (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={enableDateFilter}
                onChange={(e) => toggleDateFilter(e.target.checked)}
              />
            }
            label="Enable Date Filter"
            className="mb-2"
          />
        </Box>
      )}

      {isCampaignDelivery ? null : source === LOCAL_FILE_LABEL ? (
        <Box mt={1}>
          <TextField
            label="Local File Filter Path"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            fullWidth
            helperText="Optional. Example: {email}. The email prefix before @ is used to find the matching user and return their reports. If no match is found, the full file is returned."
          />
        </Box>
      ) : (
        <PathAndParamsEditor
          source={source}
          entity={entity}
          models={models}
          path={path}
          onPathChange={setPath}
          params={params}
          onParamsChange={setParams}
          availableVariables={Object.keys(availableVariables)}
        />
      )}

      <Box display="flex" alignItems="center" gap={2}>
        {!isCampaignDelivery && source !== LOCAL_FILE_LABEL && (
          <FormControl fullWidth>
            <InputLabel>Method</InputLabel>
            <Select value={method} onChange={(e) => setMethod(e.target.value)} size="small">
              {allowedMethods.map((m: string) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <IconButton onClick={onDelete}>
          <DeleteIcon />
        </IconButton>
      </Box>

      {!isCampaignDelivery && source !== LOCAL_FILE_LABEL && method !== 'GET' && (
        <Box mt={1}>
          <Typography variant="subtitle2" gutterBottom>
            Payload (JSON)
          </Typography>
          <TextField
            value={payload ?? ''}
            onChange={(e) => setPayload(e.target.value)}
            placeholder='{"key": "value"}'
            fullWidth
            multiline
            minRows={2}
            size="small"
          />
        </Box>
      )}

      {!isCampaignDelivery && (
      <>
      <Divider sx={{ my: 2 }} />
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography variant="subtitle2">Runtime Parameters</Typography>
          <Button
            onClick={() => setShowRuntimeParams(!showRuntimeParams)}
            variant="outlined"
            size="small"
          >
            {showRuntimeParams ? 'Hide' : 'Show'}
          </Button>
        </Box>

        {showRuntimeParams && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Runtime parameters are injected at render time from the component context.
            </Typography>

            {runtimeParamEntries.map(([key, value], idx) => (
              <Box key={idx} display="flex" alignItems="center" gap={1} mb={1}>
                <TextField
                  label="Parameter Key"
                  value={key}
                  size="small"
                  onChange={(e) => handleRuntimeParamKeyChange(idx, e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Variable/Value"
                  value={value}
                  size="small"
                  onChange={(e) => handleRuntimeParamValueChange(idx, e.target.value)}
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => handleRemoveRuntimeParam(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Button onClick={handleAddRuntimeParam} size="small" variant="outlined" sx={{ mt: 1 }}>
              Add Runtime Parameter
            </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={!!conditionalExecution}
              onChange={(e) => {
                setConditionalExecution(
                  e.target.checked
                    ? { whenField: 'domain', equals: 'salesforce' }
                    : undefined
                );
              }}
            />
          }
          label="Conditional Execution"
        />

        {conditionalExecution && (
          <Box display="flex" gap={2} mt={1} alignItems="center">
            <Typography variant="caption" sx={{ minWidth: 60 }}>
              Execute only when:
            </Typography>
            <TextField
              label="Context Field"
              value={conditionalExecution.whenField || ''}
              onChange={(e) =>
                setConditionalExecution({
                  ...conditionalExecution,
                  whenField: e.target.value,
                })
              }
              size="small"
              sx={{ flex: 1 }}
            />
            <Typography variant="caption">equals</Typography>
            <TextField
              label="Value"
              value={conditionalExecution.equals ?? ''}
              onChange={(e) =>
                setConditionalExecution({
                  ...conditionalExecution,
                  equals: e.target.value,
                })
              }
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
        )}
      </Box>
      </>
      )}

      <Box mt={1}>
        <Button variant="outlined" color="primary" onClick={handlePreview}>
          Preview
        </Button>
      </Box>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Request Preview
          {previewResp?.status && (
            <span style={{ marginLeft: 16, fontSize: 15 }}>
              {previewResp.status} {previewResp.statusText}
            </span>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <b>URL:</b> {previewReq?.url}
            <span style={{ marginLeft: 16 }}>
              <b>Method:</b> {previewReq?.method}
            </span>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="request-response-tabs">
            {tabLabels.map((label, idx) => (
              <Tab
                key={label}
                label={label}
                id={`mui-tab-${idx}`}
                aria-controls={`muitabpanel-${idx}`}
              />
            ))}
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {tab === 0 &&
              (previewLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : (
                <JsonView
                  value={previewReq?.headers || {}}
                  keyName={null as any}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard
                />
              ))}

            {isNotGet && tab === 1 &&
              (previewLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : (
                <JsonView
                  value={
                    typeof previewReq?.body === 'string'
                      ? { body: previewReq.body }
                      : previewReq?.body || {}
                  }
                  keyName={null as any}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard
                />
              ))}

            {((isNotGet && tab === 2) || (!isNotGet && tab === 1)) &&
              (previewLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : (
                <JsonView
                  value={previewResp?.headers || {}}
                  keyName={null as any}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard
                />
              ))}

            {((isNotGet && tab === 3) || (!isNotGet && tab === 2)) &&
              (previewLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : previewResp?.error ? (
                <div style={{ color: 'red' }}>{previewResp.error}</div>
              ) : (
                <JsonView
                  value={previewResp?.body || {}}
                  keyName={null as any}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard
                />
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueryRow;